import { concat, filter, find, flatMap, get, last, map, split } from 'lodash';
import Tree, { TreeProps } from 'rc-tree';
import { DataNode } from 'rc-tree/es/interface';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { getTabs } from '@store/reducers/tabsSlice';
import { MenuWrapper } from '@styles/Admin-tools/MenuEditorStyles';

import 'rc-tree/assets/index.css';
import { getDragDropPosData, insertChildItem, insertItem } from '../helpers';
import { EMenuItemType, IChildMenuItem, IMenuItem, TAnyItem, TNullableAnyItem } from '../types';
import { TreeItemTitle } from './TreeItemTitle';

type TTreeNodeData = DataNode & Pick<TAnyItem, 'menuItemType' | 'parentId'>;

interface IProps {
  menuItems: IMenuItem[];
  menuItemsValues: IMenuItem[];
  expandedKeys: string[];
  selectedKeys: string[];
  setExpanded: (expanded: string[]) => void;
  setSelectedMenuItem: (menuItem: TNullableAnyItem) => void;
  setMenuItemsValues: (menuItems: IMenuItem[]) => void;
  setDisabledIds: React.Dispatch<React.SetStateAction<string[] | undefined>>;
}

export const MenuEditorTree = ({
  menuItems,
  menuItemsValues,
  expandedKeys,
  selectedKeys,
  setDisabledIds,
  setExpanded,
  setSelectedMenuItem,
  setMenuItemsValues,
}: IProps) => {
  const tabs = useSelector(getTabs);

  const treeData = useMemo<TTreeNodeData[]>(
    () =>
      map<IMenuItem, TTreeNodeData>(menuItems, (menuItem) => ({
        key: menuItem.id,
        menuItemType: menuItem.menuItemType,
        parentId: menuItem.parentId,
        title: <TreeItemTitle {...menuItem} />,
        elementKey: menuItem?.elementKey,
        children:
          menuItem.menuItemType === EMenuItemType.Expandable
            ? map<IChildMenuItem, TTreeNodeData>(menuItem.childMenuItems, (childMenuItem) => {
                const itemUrl = menuItem?.link ? menuItem.link : '/' + menuItem?.text?.replaceAll(' ', '-').toLowerCase();

                const fullUrl =
                  !childMenuItem?.link && childMenuItem.menuItemType === 'Queue'
                    ? `${String(itemUrl)}/${(childMenuItem.text || '').replaceAll(' ', '-').toLowerCase()}`
                    : itemUrl + childMenuItem.link;

                const isMenuEditor = childMenuItem?.elementKey === 'MenuEditor';

                const disableNode = tabs?.find((tab) => tab.route?.split('?')?.[0] === fullUrl) && !isMenuEditor;
                
                if (disableNode) {
                  setDisabledIds(prevIds => {
                    const uniqueIds = new Set(prevIds ?? []);
                    uniqueIds.add(childMenuItem.id);
                    return [...uniqueIds];
                  });
                } else {
                  setDisabledIds(prevIds => prevIds?.filter(id => id !== childMenuItem.id));
                }
                
                return {
                  key: childMenuItem.id,
                  title: (
                    <TreeItemTitle
                      disabled={!disableNode}
                      {...childMenuItem}
                    />
                  ),
                  menuItemType: childMenuItem.menuItemType,
                  parentId: menuItem.id,
                  disabled: !!disableNode,
                  elementKey: childMenuItem?.elementKey,
                };
              })
            : [],
      })),
    [menuItems, tabs]
  );

  const onExpand = useCallback<NonNullable<TreeProps['onExpand']>>(
    (expandedKeys) => setExpanded(expandedKeys as string[]),
    [setExpanded],
  );

  const onSelect = useCallback<NonNullable<TreeProps['onSelect']>>(
    (selectedKeys) => {
      const [currentKey] = selectedKeys as string[];

      const allItems: TAnyItem[] = concat<TAnyItem>(
        menuItemsValues,
        flatMap<IMenuItem, IChildMenuItem>(menuItemsValues, ({ childMenuItems }) => childMenuItems),
      );

      setSelectedMenuItem(find<TAnyItem>(allItems, ['id', currentKey]) ?? null);
    },
    [menuItemsValues, setSelectedMenuItem],
  );

  const onDrop = useCallback<NonNullable<TreeProps<TTreeNodeData>['onDrop']>>(
    ({ dragNode, node, ...info }) => {
      const nodeData = dragNode as unknown as IChildMenuItem;
      if (nodeData?.elementKey === 'MenuEditor') {
        Notistack.generalError('Cannot change the position of menu editor.');
        return;
      }

      const dropPosition = info.dropPosition - Number(last<string>(split(node.pos, '-')));

      const [dragParentIdx, dragChildIdx] = getDragDropPosData(dragNode.pos);
      const [dropParentIdx, dropChildIdx] = getDragDropPosData(node.pos);

      const dragItemPath: string =
        !!dragChildIdx || dragChildIdx === 0
          ? `[${dragParentIdx}].childMenuItems[${dragChildIdx}]`
          : `[${dragParentIdx}]`;

      const dragItem: TAnyItem = get(menuItemsValues, dragItemPath);

      let updatedMenuItems: IMenuItem[] = [];
      const filteredMenuItems: IMenuItem[] =
        dragChildIdx || dragChildIdx === 0
          ? map<IMenuItem, IMenuItem>(menuItemsValues, (menuItem, index) =>
              index === dragParentIdx
                ? {
                    ...menuItem,
                    childMenuItems: filter<IChildMenuItem>(menuItem.childMenuItems, ({ id }) => id !== dragItem.id),
                  }
                : menuItem,
            )
          : filter<IMenuItem>(menuItemsValues, ({ id }) => id !== dragItem.id);

      if (dropPosition === 0) {
        // Drop on the content
        if (dragNode.menuItemType === EMenuItemType.Expandable || !!node.parentId) {
          return;
        }

        updatedMenuItems = insertChildItem(filteredMenuItems, dragItem, dragParentIdx, dropChildIdx || 0);
      } else {
        // Drop on the gap (insert before (dropPosition === -1) or insert after (dropPosition === 1))
        let parentIndex: number;
        let childIndex: number;

        if (dropChildIdx || dropChildIdx === 0) {
          parentIndex = dropParentIdx;
          childIndex = dropPosition === -1 ? dropChildIdx : dropChildIdx + 1;

          if (dragParentIdx === dropParentIdx && (dragChildIdx || dragChildIdx === 0) && dragChildIdx <= dropChildIdx) {
            childIndex -= 1;
          }

          updatedMenuItems = insertChildItem(filteredMenuItems, dragItem, parentIndex, childIndex);
        } else {
          parentIndex = dropPosition === -1 ? dropParentIdx : dropParentIdx + 1;

          if (dragParentIdx <= dropParentIdx) {
            parentIndex -= 1;
          }

          const newMenuItem: IMenuItem = {
            ...dragItem,
            parentId: null,
            childMenuItems: 'childMenuItems' in dragItem ? dragItem.childMenuItems : [],
          };

          updatedMenuItems = insertItem<IMenuItem>(filteredMenuItems, newMenuItem, parentIndex);
        }
      }

      setMenuItemsValues(updatedMenuItems);
    },
    [setMenuItemsValues, menuItemsValues],
  );

  return (
    <MenuWrapper>
      <Tree
        draggable
        treeData={treeData}
        showIcon={false}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeys}
        onExpand={onExpand}
        onSelect={onSelect}
        onDrop={onDrop}
      />
    </MenuWrapper>
  );
};
